type ClientCrashPayload = {
  message: string;
  stack?: string;
  type: "error" | "unhandledrejection";
  url: string;
  user_agent: string;
};

const ENDPOINT = "/client-errors";
const RECENT_TTL_MS = 5000;
const recentReports = new Map<string, number>();

let installed = false;

const serializeReason = (reason: unknown) => {
  if (reason instanceof Error) {
    return {
      message: reason.message || reason.name,
      stack: reason.stack,
    };
  }

  if (typeof reason === "string") {
    return { message: reason };
  }

  try {
    return { message: JSON.stringify(reason) };
  } catch {
    return { message: String(reason) };
  }
};

const reportClientCrash = (payload: ClientCrashPayload) => {
  const key = `${payload.type}:${payload.message}:${payload.stack ?? ""}`;
  const now = Date.now();
  const lastSeenAt = recentReports.get(key);

  if (lastSeenAt && now - lastSeenAt < RECENT_TTL_MS) return;

  recentReports.set(key, now);

  const body = JSON.stringify(payload);

  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: "application/json" });
    if (navigator.sendBeacon(ENDPOINT, blob)) return;
  }

  void fetch(ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    credentials: "include",
    keepalive: true,
  }).catch(() => {
    // Avoid recursive crash reporting from the reporter itself.
  });
};

export const installClientCrashReporter = () => {
  if (installed || import.meta.env.SSR) return;

  installed = true;

  window.addEventListener("error", (event) => {
    const serialized = serializeReason(event.error ?? event.message);

    reportClientCrash({
      message: serialized.message || "Unhandled browser error",
      stack: serialized.stack,
      type: "error",
      url: window.location.href,
      user_agent: navigator.userAgent,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const serialized = serializeReason(event.reason);

    reportClientCrash({
      message: serialized.message || "Unhandled promise rejection",
      stack: serialized.stack,
      type: "unhandledrejection",
      url: window.location.href,
      user_agent: navigator.userAgent,
    });
  });
};
